import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  providerKindSchema,
  getSavedProviderId,
  useCreateApiProvider,
  useUpdateApiProvider,
  useApiProvider,
  type ApiProvider,
} from "@/api/api-providers";
import { X, Eye, EyeOff, FileText, Code } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const formSchema = z.object({
  kind: providerKindSchema,
  code: z
    .string()
    .min(1, "Code is required")
    .regex(/^[a-z][a-z0-9_]*$/, "Code must be lowercase with underscores"),
  name: z.string().min(1, "Name is required"),
  baseUrl: z.string().url("Must be a valid URL"),
  supportedCarrierCodes: z.array(z.string()),
  active: z.boolean(),
  credentials: z.record(z.string()).optional(),
});

type FormData = z.infer<typeof formSchema>;

interface ApiProviderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: ApiProvider | null;
}

// Define credential fields for each provider
const getProviderCredentialFields = (code: string) => {
  switch (code) {
    case "chitchats":
      return [
        {
          name: "clientId",
          label: "Client ID",
          type: "text",
          required: true,
          envKey: "CHITCHATS_CLIENT_ID",
        },
        {
          name: "apiToken",
          label: "API Token",
          type: "password",
          required: true,
          envKey: "CHITCHATS_API_TOKEN",
        },
      ];
    case "shipstation":
      return [
        {
          name: "apiKey",
          label: "API Key",
          type: "password",
          required: true,
          envKey: "SHIPSTATION_API_KEY",
        },
        {
          name: "apiSecret",
          label: "API Secret",
          type: "password",
          required: true,
          envKey: "SHIPSTATION_API_SECRET",
        },
      ];
    case "easypost":
      return [
        {
          name: "apiKey",
          label: "API Key",
          type: "password",
          required: true,
          envKey: "EASYPOST_API_KEY",
        },
      ];
    case "canada_post_direct":
      return [
        {
          name: "username",
          label: "Username",
          type: "text",
          required: true,
          envKey: "CANADA_POST_USERNAME",
        },
        {
          name: "password",
          label: "Password",
          type: "password",
          required: true,
          envKey: "CANADA_POST_PASSWORD",
        },
        {
          name: "customerNumber",
          label: "Customer Number",
          type: "text",
          required: true,
          envKey: "CANADA_POST_CUSTOMER_NUMBER",
        },
      ];
    default:
      return [];
  }
};

// Parse environment variables from text input
const parseEnvVariables = (envText: string): Record<string, string> => {
  const credentials: Record<string, string> = {};
  const lines = envText.split("\n");

  lines.forEach((line) => {
    // Skip comments and empty lines
    if (!line || line.trim().startsWith("#") || !line.includes("=")) return;

    const [key, ...valueParts] = line.split("=");
    const value = valueParts.join("=").trim();

    // Remove quotes if present
    const cleanValue = value.replace(/^["']|["']$/g, "");

    if (key && cleanValue) {
      credentials[key.trim()] = cleanValue;
    }
  });

  return credentials;
};

// Map environment variables to credential fields
const mapEnvToCredentials = (
  envVars: Record<string, string>,
  code: string
): Record<string, string> => {
  const fields = getProviderCredentialFields(code);
  const credentials: Record<string, string> = {};

  fields.forEach((field) => {
    const envValue = envVars[field.envKey];
    if (envValue) {
      credentials[field.name] = envValue;
    }
  });

  return credentials;
};

// Generate example env format for provider
const getProviderEnvExample = (code: string): string => {
  const fields = getProviderCredentialFields(code);
  const examples = fields.map(
    (field) => `${field.envKey}=your_${field.name.toLowerCase()}_here`
  );
  return examples.join("\n");
};

export const ApiProviderDialog = ({
  open,
  onOpenChange,
  provider,
}: ApiProviderDialogProps) => {
  const createMutation = useCreateApiProvider();
  const updateMutation = useUpdateApiProvider();
  const [showCredentials, setShowCredentials] = useState<
    Record<string, boolean>
  >({});
  const [envInput, setEnvInput] = useState("");
  const [savedProviderId, setSavedProviderId] = useState<string>();
  const providerId = provider?.id ?? savedProviderId;

  useEffect(() => {
    if (!open) {
      setSavedProviderId(undefined);
      setShowCredentials({});
    }
  }, [open]);

  // Fetch provider details with credentials when editing
  const {
    data: providerWithCredentials,
    isError: detailsFailed,
    isFetching: detailsLoading,
    refetch: reloadDetails,
  } = useApiProvider(provider?.id || "", open);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      kind: undefined,
      code: "",
      name: "",
      baseUrl: "",
      supportedCarrierCodes: [],
      active: true,
      credentials: {},
    },
  });

  const credentialFields = getProviderCredentialFields(form.watch("code"));
  const credentials = form.watch("credentials");
  const missingCredentials = credentialFields
    .filter((field) => field.required && !credentials?.[field.name]?.trim())
    .map((field) => field.label);

  useEffect(() => {
    if (!open) return;
    // Use providerWithCredentials if available (when editing), otherwise use provider
    const dataToUse = providerWithCredentials ?? provider;

    if (dataToUse) {
      const credentials = dataToUse.credentials ?? {};
      form.reset({
        kind: dataToUse.kind,
        code: dataToUse.code,
        name: dataToUse.name,
        baseUrl: dataToUse.baseUrl,
        supportedCarrierCodes: dataToUse.supportedCarrierCodes || [],
        active: dataToUse.active,
        credentials,
      });

      // If we have credentials, also update the individual fields
      if (credentials && typeof credentials === "object") {
        Object.entries(credentials).forEach(([key, value]) => {
          form.setValue(`credentials.${key}`, value);
        });
      }
    } else if (!provider) {
      form.reset({
        kind: undefined,
        code: "",
        name: "",
        baseUrl: "",
        supportedCarrierCodes: [],
        active: true,
        credentials: {},
      });
    }
    // Clear env input when dialog opens
    setEnvInput("");
  }, [providerWithCredentials, provider, form, open]);

  const onSubmit = async (data: FormData) => {
    try {
      if (providerId) {
        await updateMutation.mutateAsync({
          id: providerId,
          data,
        });
        toast.success("API provider updated successfully");
      } else {
        await createMutation.mutateAsync(data);
        toast.success("API provider created successfully");
      }
      onOpenChange(false);
      form.reset();
      setEnvInput("");
    } catch (error) {
      const savedId = getSavedProviderId(error);
      if (savedId) {
        setSavedProviderId(savedId);
        return;
      }
      toast.error(
        provider
          ? "Failed to update API provider"
          : "Failed to create API provider"
      );
    }
  };

  const handleAddCarrier = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const input = e.currentTarget;
      const value = input.value.trim().toUpperCase();

      if (value && !form.watch("supportedCarrierCodes").includes(value)) {
        form.setValue("supportedCarrierCodes", [
          ...form.watch("supportedCarrierCodes"),
          value,
        ]);
        input.value = "";
      }
    }
  };

  const handleRemoveCarrier = (carrier: string) => {
    form.setValue(
      "supportedCarrierCodes",
      form.watch("supportedCarrierCodes").filter((c) => c !== carrier)
    );
  };

  const handleEnvImport = () => {
    if (!envInput || !form.watch("code")) return;

    const envVars = parseEnvVariables(envInput);
    const credentials = mapEnvToCredentials(envVars, form.watch("code"));

    // Update form credentials
    Object.entries(credentials).forEach(([key, value]) => {
      form.setValue(`credentials.${key}`, value);
    });

    // Also update the credentials object directly
    form.setValue("credentials", credentials);

    toast.success(`Imported ${Object.keys(credentials).length} credential(s)`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {providerId ? "Edit API Provider" : "Add API Provider"}
          </DialogTitle>
          <DialogDescription>
            {provider
              ? "Update the API provider configuration"
              : "Configure a new API provider for carrier integrations"}
          </DialogDescription>
        </DialogHeader>

        {provider && detailsFailed && (
          <div role="alert" className="space-y-2 text-sm text-destructive">
            <p>Could not load provider details. Retry before saving.</p>
            <Button
              type="button"
              variant="outline"
              onClick={() => void reloadDetails()}
            >
              Retry loading
            </Button>
          </div>
        )}

        {savedProviderId && (
          <p role="alert" className="text-sm text-destructive">
            Settings were saved, but could not be applied. Retry applying
            settings to this provider. If you close this form, reopen the
            provider to retry.
          </p>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code</FormLabel>
                  <FormControl>
                    {providerId ? (
                      <Input
                        {...field}
                        placeholder="chitchats"
                        disabled={true}
                      />
                    ) : (
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          // Auto-populate name and baseUrl based on selection
                          switch (value) {
                            case "chitchats":
                              form.setValue("name", "ChitChats");
                              form.setValue(
                                "baseUrl",
                                "https://staging.chitchats.com/api/v1"
                              );
                              form.setValue("supportedCarrierCodes", [
                                "chitchats",
                              ]);
                              break;
                            case "shipstation":
                              form.setValue("name", "ShipStation");
                              form.setValue(
                                "baseUrl",
                                "https://ssapi.shipstation.com"
                              );
                              form.setValue("supportedCarrierCodes", [
                                "ups",
                                "fedex",
                                "canada_post",
                                "usps",
                              ]);
                              break;
                          }
                        }}
                        value={field.value}
                      >
                        <SelectTrigger aria-label="Code">
                          <SelectValue placeholder="Select a provider type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="chitchats">ChitChats</SelectItem>
                          <SelectItem value="shipstation">
                            ShipStation
                          </SelectItem>
                          <SelectItem value="easypost" disabled>
                            EasyPost — unavailable
                          </SelectItem>
                          <SelectItem value="canada_post_direct" disabled>
                            Canada Post Direct — unavailable
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </FormControl>
                  <FormDescription>
                    {providerId
                      ? "Provider type cannot be changed"
                      : "Select the API provider type"}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {provider &&
              !["chitchats", "shipstation"].includes(provider.code) && (
                <p role="status" className="text-sm text-amber-700">
                  This integration has no shipping adapter. Saved settings do
                  not enable quotes or labels.
                </p>
              )}

            <FormField
              control={form.control}
              name="kind"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Provider classification</FormLabel>
                  <Select
                    value={field.value ?? ""}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a classification" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="AGGREGATOR">Aggregator</SelectItem>
                      <SelectItem value="DIRECT">Direct carrier</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Aggregator: integrates multiple carriers; customer-owned
                    accounts are not allowed. Direct carrier: connects to a
                    carrier's own API; customers may connect their own accounts.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="ChitChats" />
                  </FormControl>
                  <FormDescription>
                    Display name for the provider
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="baseUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Base URL</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="url"
                      placeholder="https://api.example.com"
                    />
                  </FormControl>
                  <FormDescription>API endpoint base URL</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="supportedCarrierCodes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Supported Carriers</FormLabel>
                  <FormControl>
                    <div className="space-y-2">
                      <Input
                        placeholder="Enter carrier code and press Enter"
                        onKeyDown={handleAddCarrier}
                      />
                      <div className="flex flex-wrap gap-2">
                        {field.value.map((carrier) => (
                          <Badge
                            key={carrier}
                            variant="secondary"
                            className="flex items-center gap-1"
                          >
                            {carrier}
                            <X
                              className="h-3 w-3 cursor-pointer hover:text-red-500"
                              onClick={() => handleRemoveCarrier(carrier)}
                            />
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </FormControl>
                  <FormDescription>
                    Carrier codes this provider supports
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Credential Fields */}
            {form.watch("code") &&
              (credentialFields.length > 0 ? (
                <div className="space-y-4">
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium">API Credentials</h4>
                    </div>
                    {!(provider && (detailsLoading || detailsFailed)) && (
                      <p
                        role="status"
                        className="mb-3 text-sm text-muted-foreground"
                      >
                        {missingCredentials.length > 0
                          ? `Missing required credentials: ${missingCredentials.join(", ")}.`
                          : "Required credential fields are filled in."}{" "}
                        Saving these fields does not verify provider access.
                      </p>
                    )}
                    <Tabs defaultValue="form" className="w-full">
                      <TabsList className="grid w-full grid-cols-2">
                        <TabsTrigger value="form">
                          <FileText className="h-4 w-4 mr-2" />
                          Form Fields
                        </TabsTrigger>
                        <TabsTrigger value="env">
                          <Code className="h-4 w-4 mr-2" />
                          Env Import
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="form" className="space-y-3 mt-4">
                        {credentialFields.map((field) => {
                          const hasValue = !!credentials?.[field.name]?.trim();
                          return (
                            <FormField
                              key={field.name}
                              control={form.control}
                              name={`credentials.${field.name}`}
                              defaultValue=""
                              render={({ field: formField }) => (
                                <FormItem>
                                  <FormLabel>
                                    {field.label}
                                    {field.required && (
                                      <span className="text-red-500 ml-1">
                                        *
                                      </span>
                                    )}
                                  </FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <Input
                                        {...formField}
                                        value={formField.value ?? ""}
                                        aria-label={field.label}
                                        type={
                                          field.type === "password" &&
                                          !showCredentials[field.name]
                                            ? "password"
                                            : "text"
                                        }
                                        placeholder={
                                          hasValue
                                            ? "••••••••••••••••"
                                            : `Enter ${field.label.toLowerCase()}`
                                        }
                                      />
                                      {field.type === "password" && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setShowCredentials((prev) => ({
                                              ...prev,
                                              [field.name]: !prev[field.name],
                                            }))
                                          }
                                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                        >
                                          {showCredentials[field.name] ? (
                                            <EyeOff className="h-4 w-4" />
                                          ) : (
                                            <Eye className="h-4 w-4" />
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          );
                        })}
                      </TabsContent>

                      <TabsContent value="env" className="space-y-4 mt-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            Paste Environment Variables
                          </label>
                          <Textarea
                            placeholder={getProviderEnvExample(
                              form.watch("code")
                            )}
                            value={envInput}
                            onChange={(e) => setEnvInput(e.target.value)}
                            className="font-mono text-sm h-32"
                          />
                          <p className="text-xs text-muted-foreground">
                            Paste your .env file content here. We'll extract the
                            relevant variables.
                          </p>
                        </div>
                        <Button
                          type="button"
                          onClick={handleEnvImport}
                          variant="secondary"
                          className="w-full"
                        >
                          <Code className="h-4 w-4 mr-2" />
                          Import Environment Variables
                        </Button>
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>
              ) : (
                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No credentials required for this provider type
                  </p>
                </div>
              ))}

            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between space-x-2">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Enable or disable this provider
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  createMutation.isPending ||
                  updateMutation.isPending ||
                  (!!provider &&
                    (!providerWithCredentials ||
                      detailsFailed ||
                      detailsLoading))
                }
              >
                {createMutation.isPending || updateMutation.isPending
                  ? "Saving..."
                  : savedProviderId
                    ? "Retry applying settings"
                    : provider
                      ? "Update"
                      : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
